import React, { useState, useMemo } from "react";
import { withTheme } from "styled-components";
import ButtonDropdown from "../../../ui-library/ButtonDropdown/ButtonDropdown";
import { useTranslation } from "react-i18next";
import { isRtl } from "../../../../i18n/helpers";
import history from "../../../../modules/history";
import CustomModal, {
  ModalSize,
} from "../../../ui-library/CustomModal/CustomModal";
import ReportGeneratorOptions from "../../ReportGeneratorOptions/ReportGeneratorOptions";
import SmartEditParticipant from "../../../smart/EditParticipant/EditParticipant";
import SmartStudyStatusHistory from "../../../smart/StudyStatusHistory/StudyStatusHistory";
import SmartChangeStudyStatus from "../../../smart/ChangeStudyStatus/ChangeStudyStatus";
import SmartPersonalData from "../../../smart/PersonalData/PersonalData";
import DietDiary from "../../../smart/DietDiary/DietDiary";
import {
  DietDairyTypes,
  dietDairyTypeToString,
} from "../../../smart/DietDiary/DietDiary.constants";
import moment from "moment";
import {
  getStudyStatusAvailableOptions,
  studyStatusOptions,
} from "../../../../modules/constants";
import AlertUGNX from "../../../ToastsAndAlerts/AlertUGNX";
import { AlertType } from "../../../ToastsAndAlerts/constants";

const dietDiaryOption = {
  text: dietDairyTypeToString(DietDairyTypes.FOOD_DRINK),
  type: DietDairyTypes.FOOD_DRINK,
};

const ActionsButton = (props) => {
  const {
    canChangeStudyStatus,
    canViewStudyStatus,
    canEditParticipant,
    canGenerateReport,
    canManageUsers,
    participant,
    fetchReportEntriesData,
    studyId,
    canViewParticipantPage,
    canViewPersonalData,
    onRefreshData,
    canCreate24hRecall,
    canCreatePaperRecapture,
    fetchDailyTargetHistory,
  } = props;

  const { t } = useTranslation();

  const [showReportGeneratorModal, setShowReportGeneratorModal] =
    useState(false);
  const [showEditParticipantModal, setShowEditParticipantModal] =
    useState(false);
  const [showChangeStudyStatusModal, setShowChangeStudyStatusModal] =
    useState(false);
  const [showPersonalDataModal, setShowPersonalDataModal] = useState(false);
  const [showStudyStatusHistoryModal, setShowStudyStatusHistoryModal] =
    useState(false);

  const [add24hRecallEntry, setAdd24hRecallEntry] = useState(false);
  const [addPaperRecapture, setAddPaperRecapture] = useState(false);

  const minDateMeal = useMemo(() => {
    return moment(participant?.dateOfEnrollment).toDate();
  }, [participant]);

  const onCloseDietDiaryModal = () => {
    setAdd24hRecallEntry(false);
    setAddPaperRecapture(false);

    onRefreshData();
  };

  const closeReportGeneratorModal = () => {
    setShowReportGeneratorModal(false);
  };

  const openGenerateReportOptions = () => {
    setShowReportGeneratorModal(true);
  };

  const openEditParticipantModal = () => {
    setShowEditParticipantModal(true);
  };

  const closeEditParticipantModal = (refreshData) => {
    setShowEditParticipantModal(false);

    if (refreshData) {
      onRefreshData();
    }
  };

  const closeChangeStudyStatusModal = (refreshData) => {
    setShowChangeStudyStatusModal(false);

    if (refreshData) {
      onRefreshData();
    }
  };

  const changeStudyStatus = () => {
    setShowChangeStudyStatusModal(true);
  };

  const closeStudyStatusHistoryModal = () => {
    setShowStudyStatusHistoryModal(false);
  };

  const openStudyStatusHistory = () => {
    setShowStudyStatusHistoryModal(true);
  };

  const showPersonalData = () => {
    setShowPersonalDataModal(true);
  };

  const closePersonalDataModal = () => {
    setShowPersonalDataModal(false);
  };

  const getCurrentStudyStatus = (studyStatusId) => {
    return (
      studyStatusOptions[
        Object.keys(studyStatusOptions).find(
          (statusKey) => studyStatusOptions[statusKey].id === studyStatusId
        )
      ] || []
    );
  };

  const actionOptions = useMemo(() => {
    return {
      VIEW_PARTICIPANT: {
        id: "view_participant",
        text: "View Participant",
        isVisible: canViewParticipantPage,
        onClick: () => history.push(`/stats/${participant?.participantId}`),
      },
      ADD_24H_RECALL: {
        id: "add_24h_recall",
        text: t("appText.actions.add.24h.recall"),
        type: "add",
        isVisible: canCreate24hRecall,
        onClick: () => setAdd24hRecallEntry(true),
      },
      ADD_PAPER_RECAPTURE: {
        id: "add_paper_recapture",
        text: t("appText.actions.add.paper.recapture"),
        type: "add",
        isVisible: canCreatePaperRecapture,
        onClick: () => setAddPaperRecapture(true),
      },
      CHANGE_STUDY_STATUS: {
        id: "change_study_status",
        text: t("appText.actions.change.study.status"),
        type: "change_study_status",
        isVisible: canChangeStudyStatus,
        isDisabled:
          getStudyStatusAvailableOptions(
            getCurrentStudyStatus(participant?.studyStatusId)
          ) === null,
        onClick: () => changeStudyStatus(),
      },
      STUDY_STATUS_HISTORY: {
        id: "study_status_history",
        text: t("appText.actions.study.status.history"),
        type: "study_status_history",
        isVisible: canViewStudyStatus,
        onClick: () => openStudyStatusHistory(),
      },
      EDIT_PARTICIPANT: {
        id: "edit_participant",
        text: t("appText.actions.edit.participant"),
        type: "edit_participant",
        isVisible: canEditParticipant,
        onClick: () => openEditParticipantModal(),
      },
      VIEW_PERSONAL_DATA: {
        id: "view_personal_data",
        text: "View Personal Data",
        isVisible: canViewPersonalData,
        onClick: () => showPersonalData(),
      },
      GENERATE_REPORT: {
        id: "generate_report",
        text: t("appText.actions.generate.report"),
        type: "generate_report",
        isVisible: canGenerateReport,
        onClick: () => openGenerateReportOptions(),
      },
      MANAGE_CAREGIVERS: {
        id: "manage_caregivers",
        text: t("appText.participantUsers.manageCaregivers"),
        type: "manage_caregivers",
        isVisible: canManageUsers,
        onClick: () => {
          history.push(
            `/caregivers/${participant?.userName}/${participant?.participantId}`
          );
        },
      },
    };
  }, [
    participant,
    canViewParticipantPage,
    canCreate24hRecall,
    canViewStudyStatus,
    canEditParticipant,
    canViewPersonalData,
    canGenerateReport,
    canManageUsers,
  ]);

  return (
    <>
      <CustomModal
        show={add24hRecallEntry}
        title={t("appText.actions.add.24h.recall")}
        size={ModalSize.large}
        onClose={() => setAdd24hRecallEntry(false)}
      >
        <DietDiary
          is24hRecall={true}
          type={DietDairyTypes.FOOD_DRINK}
          dietDiaryOption={dietDiaryOption}
          onSubmit={() => onCloseDietDiaryModal()}
          maxDate={moment().toDate()}
          minDate={minDateMeal}
          participantId={participant?.participantId}
          participantLanguage={participant?.language}
        />
      </CustomModal>

      <CustomModal
        show={addPaperRecapture}
        title={t("appText.actions.add.paper.recapture")}
        size={ModalSize.large}
        onClose={() => setAddPaperRecapture(false)}
      >
        <AlertUGNX type={AlertType.WARNING}>
          Reminder: Only enter data from IRB-approved paper diaries. Contact
          your CRA if unsure.
        </AlertUGNX>
        <DietDiary
          isPaperRecapture={true}
          type={DietDairyTypes.FOOD_DRINK}
          dietDiaryOption={dietDiaryOption}
          onSubmit={() => onCloseDietDiaryModal()}
          maxDate={moment().toDate()}
          minDate={minDateMeal}
          participantId={participant?.participantId}
          participantLanguage={participant?.language}
        />
      </CustomModal>

      <CustomModal
        show={showPersonalDataModal}
        title="Personal Data"
        onClose={closePersonalDataModal}
      >
        <SmartPersonalData
          onClose={closePersonalDataModal}
          participantId={participant?.participantId}
        />
      </CustomModal>

      <CustomModal
        show={showReportGeneratorModal}
        title="Generate Report"
        onClose={closeReportGeneratorModal}
      >
        <ReportGeneratorOptions
          fetchEntriesData={fetchReportEntriesData}
          fetchDailyTargetHistory={fetchDailyTargetHistory}
          participant={participant}
        />
      </CustomModal>

      <CustomModal
        show={showEditParticipantModal}
        title="Edit Participant"
        onClose={() => setShowEditParticipantModal(false)}
      >
        <SmartEditParticipant
          {...{ studyId }}
          participant={participant}
          onUpdate={() => closeEditParticipantModal(true)}
        />
      </CustomModal>

      <CustomModal
        show={showChangeStudyStatusModal && !!participant}
        title={`Changing ${participant?.userName ?? ""} Study Status`}
        onClose={() => closeChangeStudyStatusModal(false)}
      >
        <SmartChangeStudyStatus
          onUpdate={() => closeChangeStudyStatusModal(true)}
          currentDailyTargets={participant?.dailyTargets}
          studyEnrollmentID={participant?.studyEnrollmentId}
          studyStatusID={participant?.studyStatusId}
          onCancel={() => closeChangeStudyStatusModal(false)}
        />
      </CustomModal>

      <CustomModal
        show={showStudyStatusHistoryModal && !!participant}
        title={`Study Status History for ${participant?.userName ?? ""}`}
        onClose={closeStudyStatusHistoryModal}
      >
        <SmartStudyStatusHistory
          studyEnrollmentID={participant?.studyEnrollmentId}
          onClose={closeStudyStatusHistoryModal}
        />
      </CustomModal>
      <ButtonDropdown
        dataTestId={`action-btn-${participant?.userName}`}
        size="sm"
        title={t("appText.dietDiary.actions.button")}
        options={Object.values(actionOptions)}
        align={isRtl() ? "end" : "start"}
      />
    </>
  );
};

export default withTheme(ActionsButton);
